import { createStorage } from '@sg/mmkv-storage';

// ─────────────────────────────────────────────────────────────────────────────

export function addCartLineItemsToBundleAttribution(params: Params) {
  const { orderId, cartLineItemIds } = params;

  const attribution = getBundleAttribution();

  const isSameOrder = attribution.orderId === orderId;

  setAttributionState({
    orderId,
    cartLineItemIds: isSameOrder
      ? Array.from(new Set([...attribution.cartLineItemIds, ...cartLineItemIds]))
      : cartLineItemIds,
  });
}

export function getBundleAttribution() {
  const storedAttribution = BUNDLE_ATTRIBUTION_STORAGE.getString(ATTRIBUTION_STORAGE_KEY);

  if (!storedAttribution) return EMPTY_ATTRIBUTION;

  try {
    return parseAttributionState(JSON.parse(storedAttribution));
  } catch {
    return EMPTY_ATTRIBUTION;
  }
}

export function checkIfBundleCartLineItem(params: CheckIfBundleCartLineItemParams) {
  const { cartLineItemId, attribution = getBundleAttribution() } = params;

  if (!cartLineItemId) return false;

  return attribution.cartLineItemIds.includes(cartLineItemId);
}

export function clearBundleAttribution() {
  BUNDLE_ATTRIBUTION_STORAGE.remove(ATTRIBUTION_STORAGE_KEY);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setAttributionState(attribution: BundleAttributionState) {
  BUNDLE_ATTRIBUTION_STORAGE.set(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
}

function parseAttributionState(value: unknown): BundleAttributionState {
  if (!checkIfRecord(value)) {
    return EMPTY_ATTRIBUTION;
  }

  const cartLineItemIds = Array.isArray(value.cartLineItemIds)
    ? value.cartLineItemIds.filter(checkIfString)
    : [];

  return {
    orderId: checkIfString(value.orderId) ? value.orderId : undefined,
    cartLineItemIds,
  };
}

function checkIfRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function checkIfString(value: unknown): value is string {
  return typeof value === 'string';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BUNDLE_ATTRIBUTION_STORAGE = createStorage({ id: 'sg.bundle-attribution' });
const ATTRIBUTION_STORAGE_KEY = 'bundle-attribution';
const EMPTY_ATTRIBUTION: BundleAttributionState = { orderId: undefined, cartLineItemIds: [] };

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  orderId: string | undefined;
  cartLineItemIds: string[];
};

type CheckIfBundleCartLineItemParams = {
  cartLineItemId?: string;
  attribution?: BundleAttributionState;
};

type BundleAttributionState = {
  orderId: string | undefined;
  cartLineItemIds: string[];
};
